"use client";

import { useRef, useState, useEffect } from "react";
import gsap from "gsap";
import { cn } from "@/lib/utils";
import logo from "@/assets/images/logo_hero.svg";
import hero1 from "@/assets/images/hero_1.webp";
import hero2 from "@/assets/videos/hero_2.mp4";
import hero3 from "@/assets/images/hero_3.webp";
import hero4 from "@/assets/images/hero_4.webp";
import heroPreview from "@/assets/videos/hero_preview.mp4";
import { useGSAP } from "@gsap/react";
import HeroImage from "./HeroImage";
import TiltBox from "./TiltBox";
import { VideoDialog } from "@/components/ui/video-dialog";
import { useTranslation } from "react-i18next";

interface HeroSectionProps extends React.HTMLAttributes<HTMLElement> {}

function HeroSection({ className, ...props }: HeroSectionProps) {
  const heroContainerRef = useRef<HTMLDivElement>(null);
  const heroImg1Ref = useRef<HTMLDivElement>(null);
  const heroImg2Ref = useRef<HTMLDivElement>(null);
  const heroImg3Ref = useRef<HTMLDivElement>(null);
  const heroImg4Ref = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const tiltBoxRef = useRef<HTMLDivElement>(null);

  const introContainerRef = useRef<HTMLDivElement>(null);

  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const introTl = gsap.timeline();

  useGSAP(() => {
    const ctx = gsap.context( async () => {
      gsap.set(
        [
          heroImg1Ref.current, 
          heroImg2Ref.current, 
          heroImg3Ref.current, 
          heroImg4Ref.current, 
          logoRef.current,
          tiltBoxRef.current,
          introContainerRef.current,
        ],
        { opacity: 0 },
      );

      await introTl
        .fromTo(heroImg1Ref.current, { opacity: 0, x: -30 }, { opacity: 1, x: 0, duration: 0.6, ease: "power2.inOut" })
        .fromTo(heroImg2Ref.current, { opacity: 0, y: -30 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.inOut" }, "-=0.4")
        .fromTo(heroImg3Ref.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.inOut" }, "-=0.4")
        .fromTo(heroImg4Ref.current, { opacity: 0, x: 30 }, { opacity: 1, x: 0, duration: 0.6, ease: "power2.inOut" }, "-=0.4")
        .fromTo(tiltBoxRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.inOut" }, "-=0.3")
        .fromTo(logoRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.inOut" }, "<");

      if(introContainerRef.current){
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        
        gsap.fromTo(
          introContainerRef.current,
          { opacity: 0, y: isMobile ? 30 : 0 },
          { 
            opacity: 1,
            y: 0,
            scrollTrigger: isMobile ? undefined : {
              trigger: introContainerRef.current,
              start: "top center",
              end: "center center",
              scrub: 1,
            },
          },
        );
      }

      gsap.fromTo(
        heroImg1Ref.current,
        { x: 0, y: 0 },
        { 
          x: isMobile ? 0 : -40,
          y: -40,
          scrollTrigger: {
            trigger: heroContainerRef.current,
            start: "top top",
            end: "center top",
            scrub: 1,
          },
        }
      );

      gsap.fromTo(
        heroImg2Ref.current,
        { x: 0, y: 0 },
        { 
          x: isMobile ? 20 : 0,
          y: 50,
          scrollTrigger: {
            trigger: heroContainerRef.current,
            start: "top top",
            end: "center top",
            scrub: 1,
          },
        },
      );

      gsap.fromTo(
        heroImg3Ref.current,
        { x: 0, y: 0 },
        { 
          x: -40,
          y: 40,
          scrollTrigger: {
            trigger: heroContainerRef.current,
            start: "top top",
            end: "center top",
            scrub: 1,
          },
        },
      );

      gsap.fromTo(
        heroImg4Ref.current,
        { x: 0, y: 0 },
        { 
          x: isMobile ? -20 : 40,
          y: isMobile ? 0 : 40,
          scrollTrigger: {
            trigger: heroContainerRef.current,
            start: "top top",
            end: "center top",
            scrub: 1,
          },
        },
      );

      gsap.fromTo(
        logoRef.current,
        { y: 0 },
        {
          y: -100,
          scrollTrigger: {
            trigger: heroContainerRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        }
      );
      
      gsap.fromTo(
        tiltBoxRef.current,
        { y: 0 },
        {
          y: -100,
          scrollTrigger: {
            trigger: heroContainerRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        }
      );
    });

    return () => ctx.revert();
  });

  return (
    <section
      ref={heroContainerRef}
      className={cn(
        "relative flex flex-col items-center justify-center pt-4 overflow-hidden",
        "sm:pt-6",
        "md:pt-8",
        "2k:pt-12",
        "4k:pt-16",
        className
      )}
      {...props}
    >
      {/* Hero Collage Container - aspect ratio matches SVG viewBox 1319x554 */}
      <div className="relative w-[97%]" style={{ aspectRatio: isMobile ? '364 / 386' : '1319 / 554' }}>
        {/* Top-left image: x=0, y=6, width=262, height=175 */}
        <HeroImage
          ref={heroImg1Ref}
          src={hero1}
          alt="Interior living room"
          className="absolute z-1"
          style={{
            left: isMobile ? '26.92%' : '0%',
            top: isMobile ? '0%' : '1.08%',
            width: isMobile ? '32.97%' : '19.86%',
            height: isMobile ? '20.73%' : '31.59%',
            opacity: 0,
          }}
        />

        {/* Center tall image: x=378, y=6, width=413, height=548 */}
        <HeroImage
          ref={heroImg2Ref}
          videoSrc={hero2}
          alt="Modern kitchen"
          className="absolute z-1 cursor-pointer"
          onClick={() => setIsVideoOpen(true)}
          style={{
            left: isMobile ? '39.84%' : '28.66%',
            top: isMobile ? '26.68%' : '1.08%',
            width: isMobile ? '58.52%' : '31.31%',
            height: isMobile ? '73.32%' : '98.92%',
            opacity: 0,
          }}
        />

        {/* Bottom-left image: x=111, y=426, width=192, height=128 */}
        <HeroImage
          ref={heroImg3Ref}
          src={hero3}
          alt="Building exterior"
          className="absolute z-11"
          style={{
            left: isMobile ? '1.37%' : '8.42%',
            top: isMobile ? '60.1%' : '76.9%',
            width: isMobile ? '32.97%' : '14.56%',
            height: isMobile ? '20.73%' : '23.1%',
            opacity: 0,
          }}
        />

        {/* Bottom-right image: x=910, y=282, width=409, height=272 */}
        <HeroImage
          ref={heroImg4Ref}
          src={hero4}
          alt="Street view"
          className={`absolute ${isMobile ? "z-11" : "z-1"}`}
          style={{
            left: isMobile ? '14%' : '68.99%',
            top: isMobile ? '41%' :'50.9%',
            width: isMobile ? '20%' : '31.01%',
            height: isMobile ? '13%' : '49.1%',
            opacity: 0,
          }}
        />

        <img
          src={logo} 
          ref={logoRef}
          alt="Dovie Logo" 
          className={cn(
            "absolute z-10 pointer-events-none w-full",
            isMobile ? "" : "h-full top-[-40px]"
          )}
          style={{ opacity: 0, ...(isMobile ? { top: '11.66%' } : {}) }} 
        />

        <TiltBox
          ref={tiltBoxRef}
          className="absolute z-20"
          style={{
            left: '68.76%',
            width: '19.86%',
            top: isMobile ? '10%' : '0%',
            height: isMobile ? '11%' : '23.1%',
          }}
        />
      </div>

      <div ref={introContainerRef} className="max-w-[90%] md:max-w-[65%] flex flex-col px-4 mt-12">
        <p className={cn(
          isMobile ? "font-mobile-headline" : "font-paragraph",
          "tracking-normal! text-left mb-3 mt-12",
          "md:mb-4 md:mt-24",
          "2k:mb-8 2k:mt-48",
          "k:mb-8 4k:mt-64",
        )}>
          {t("hero.introParagraph")}
        </p>

        <p className={cn(
          isMobile ? "font-mobile-large-body" : "font-body",
          "text-left",
        )}>
          {t("hero.introBody")}<br/><br/>
        </p>
      </div>
      <VideoDialog 
        isOpen={isVideoOpen} 
        onClose={() => setIsVideoOpen(false)} 
        videoSrc={heroPreview} 
      />
    </section>
  );
}

export { HeroSection };
